/**
 * Sticky Shopping Footer — Product version.
 *
 * A PROXY of the product page's main buy box (the first product section, which
 * holds either a standard product form or a bundle-builder). It owns no form and
 * no purchase toggle:
 *   - It mirrors the buy box's live price + add-to-cart label/state.
 *   - Its add-to-cart click forwards to the buy box's REAL button, so the exact
 *     current selection (variant, subscription plan, bundle flavors) is added —
 *     i.e. it always reflects what the shopper chose above.
 *
 * Visibility: appears after a scroll threshold and hides once the footer reaches
 * the viewport. For a bundle, an incomplete build shows a greyed "Select N more
 * boxes" that bounces the shopper up to the builder; clicking anywhere else on
 * the bar also scrolls back up to the buy box.
 */
class StickyShoppingFooterComponent extends HTMLElement {
  connectedCallback() {
    // The buy-box section is whichever section holds the bundle builder or the
    // product form (robust across templates), falling back to the first section.
    const main = document.querySelector('#MainContent');
    this.buyBox =
      main?.querySelector('bundle-builder-component, bundle-builder-flex-component')?.closest('.shopify-section') ||
      main?.querySelector('product-form-component')?.closest('.shopify-section') ||
      main?.querySelector('.shopify-section') ||
      main;
    // Nothing to proxy — leave the bar hidden.
    if (!this.buyBox) return;

    this.abort = new AbortController();
    this.pastTrigger = false;
    this.inFooter = false;

    this.atcButton = this.querySelector('[data-sf-atc]');
    this.atcLabel = this.querySelector('[data-sf-atc-label]');
    this.priceEl = this.querySelector('[data-sf-price]');
    this.imageEl = this.querySelector('[data-sf-image]');

    this.#wireClicks();
    this.#wireScrollTrigger();
    this.#wireBundleCompletion();
    this.#wireDomMirror();

    this.#sync();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.domObserver?.disconnect();
  }

  /** The bundle builder in the buy box, if this is a bundle product. */
  get bundle() {
    return this.buyBox.querySelector('bundle-builder-component, bundle-builder-flex-component');
  }

  /** The real add-to-cart / submit button in the buy box. */
  #realAddToCart() {
    const root = this.bundle || this.buyBox;
    return (
      root.querySelector('[data-bundle-add-to-cart-button]') ||
      root.querySelector('[ref="addToCartButton"]') ||
      root.querySelector('product-form-component button[name="add"]') ||
      root.querySelector('button[name="add"]') ||
      root.querySelector('.add-to-cart-button') ||
      root.querySelector('button[type="submit"]')
    );
  }

  #wireClicks() {
    this.addEventListener(
      'click',
      (event) => {
        const onAtc = Boolean(event.target.closest('[data-sf-atc]'));
        const real = this.#realAddToCart();

        // Add to cart only when the ATC button itself is clicked and ready.
        if (onAtc && real && !real.disabled) {
          event.preventDefault();
          this.#addToCart(real);
          return;
        }

        // Anything else on the bar — the greyed "Select N more boxes" button or
        // the product info — bounces the shopper up to the buy box to keep going.
        event.preventDefault();
        this.#scrollToBuyBox();
      },
      { signal: this.abort.signal }
    );
  }

  #addToCart(real) {
    // The real button is up the page (scrolled off-screen by the time this bar
    // shows), so its own fly-to-cart would launch from out of view. Suppress it
    // for this click and replay the animation from the visible bar.
    const realComponent = real.closest('add-to-cart-component');
    const prevFlag = realComponent?.getAttribute('data-add-to-cart-animation');
    realComponent?.setAttribute('data-add-to-cart-animation', 'false');

    // Forwarding the click runs the buy box's own handler, which carries the
    // current variant, selling plan and bundle flavor properties.
    real.click();

    if (realComponent) {
      if (prevFlag == null) realComponent.removeAttribute('data-add-to-cart-animation');
      else realComponent.setAttribute('data-add-to-cart-animation', prevFlag);
    }

    this.#flyToCart();
  }

  /** Smooth-scroll up to the buy box so the shopper can adjust their selection. */
  #scrollToBuyBox() {
    const target = this.bundle || this.buyBox;
    if (!target) return;
    const top = target.getBoundingClientRect().top + window.scrollY - headerOffset();
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  /**
   * Replays the theme's "fly to cart" animation from this bar's button, reusing
   * the global <fly-to-cart> element defined by product-form.js.
   */
  #flyToCart() {
    if (!customElements.get('fly-to-cart')) return;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.imageEl ? this.imageEl.currentSrc || this.imageEl.src : null;
    if (!cartIcon || !image) return;

    const fly = document.createElement('fly-to-cart');
    fly.style.setProperty('background-image', `url(${image})`);
    fly.source = this.atcButton;
    fly.destination = cartIcon;
    document.body.appendChild(fly);
  }

  #wireScrollTrigger() {
    // Resolve the footer region once. We hide the bar once the footer's top
    // reaches the viewport and keep it hidden to the page bottom — computed live
    // on scroll rather than via IntersectionObserver, which stops firing once the
    // footer is fully past the top (that made the bar reappear at the very bottom).
    this.footerEl =
      document.querySelector('.shopify-section-group-footer-group') ||
      document.querySelector('footer') ||
      document.querySelector('[class*="footer"]:not(.sticky-shopping-footer)');

    const onScroll = () => {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const threshold = window.innerHeight * (isMobile ? 1.0 : 0.5);
      const past = window.scrollY >= threshold;
      const inFooter = this.footerEl
        ? this.footerEl.getBoundingClientRect().top <= window.innerHeight
        : false;
      if (past !== this.pastTrigger || inFooter !== this.inFooter) {
        this.pastTrigger = past;
        this.inFooter = inFooter;
        this.#updateVisibility();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true, signal: this.abort.signal });
    window.addEventListener('resize', onScroll, { passive: true, signal: this.abort.signal });
    onScroll();
  }

  #wireBundleCompletion() {
    // The bundle's real button toggles disabled + label ("Select N more boxes"
    // ↔ "Add to Gym Bag") as boxes fill. Refresh the bar's mirrored button when
    // that happens — the bar stays visible either way.
    document.addEventListener(
      'bundle:completion-changed',
      () => this.#scheduleSync(),
      { signal: this.abort.signal }
    );
  }

  /**
   * Re-mirror the bar whenever the buy box changes: variant picks, subscription
   * toggles and price updates all show up as DOM mutations in the buy box.
   */
  #wireDomMirror() {
    this.domObserver = new MutationObserver(() => this.#scheduleSync());
    this.domObserver.observe(this.buyBox, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-disabled', 'value'],
    });
  }

  #scheduleSync() {
    if (this.syncQueued) return;
    this.syncQueued = true;
    requestAnimationFrame(() => {
      this.syncQueued = false;
      this.#sync();
    });
  }

  #sync() {
    const root = this.bundle || this.buyBox;
    if (!root) return;

    // Price — clone whatever the buy box currently renders so compare pricing,
    // variant changes and subscription discounts come along for free.
    if (this.priceEl) {
      const priceNode =
        root.querySelector('.bundle-builder-block__price') ||
        root.querySelector('[ref="priceContainer"]') ||
        root.querySelector('.price__container') ||
        root.querySelector('.price');
      if (priceNode && !priceNode.hidden && priceNode.textContent.trim()) {
        this.priceEl.innerHTML = priceNode.innerHTML;
        this.priceEl.hidden = false;
      } else if (priceNode) {
        // The flex builder hides its price until the first box is added —
        // mirror that instead of holding a stale clone.
        this.priceEl.hidden = true;
      }
    }

    // Mirror the real button's label + disabled/greyed state ("Add to Gym Bag" /
    // "Select 2 more boxes" / "Sold out"). We mark it visually rather than
    // natively disabling so an incomplete click can still bounce to the buy box.
    const real = this.#realAddToCart();
    if (this.atcButton) this.atcButton.classList.toggle('is-incomplete', Boolean(real?.disabled));
    if (this.atcLabel && real) {
      const realLabel =
        real.querySelector('.add-to-cart-text__content, .bundle-builder-block__button-text') || real;
      let text = realLabel.textContent.trim();
      // Compact label on mobile: icon + "Add • $25.50/Box".
      if (text && window.matchMedia('(max-width: 749px)').matches) {
        text = text.replace(/^Add to Gym Bag\b/, 'Add');
      }
      if (text) this.atcLabel.textContent = text;
    }

    this.#updateVisibility();
  }

  #updateVisibility() {
    const visible = this.pastTrigger && !this.inFooter;
    if (visible) this.removeAttribute('hidden');
    else this.setAttribute('hidden', '');
  }
}

/** Pixels to keep clear at the top when scrolling, accounting for a sticky header. */
function headerOffset() {
  const header = document.querySelector('#header-component') || document.querySelector('.header');
  if (!header) return 16;
  const declared = getComputedStyle(header).getPropertyValue('--header-height').trim();
  const height = parseInt(declared, 10) || header.getBoundingClientRect().height || 0;
  return height + 16;
}

if (!customElements.get('sticky-shopping-footer')) {
  customElements.define('sticky-shopping-footer', StickyShoppingFooterComponent);
}
